import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import { glofMISConfig, transformGlofMISData, transformStatusDataForExcel } from './excelUtilsGlof';

const getTimestamp = () => {
    const now = new Date(new Date().getTime() + (5.5 * 60 * 60 * 1000));
    return `${now.toISOString().split("T")[0]}_${now.toISOString().split("T")[1].split(".")[0].replace(/:/g, "")}`;
};

const formatHeaderDate = (dateVal) => {
    if (!dateVal) return "NA";
    if (typeof dateVal === "string") {
        if (dateVal.includes('/')) return dateVal.replace(/\//g, "-");
        if (dateVal.match(/^\d{4}-\d{2}-\d{2}$/)) {
            const parts = dateVal.split('-');
            return `${parts[2]}-${parts[1]}-${parts[0]}`;
        }
    }
    return dateVal;
};

const applyStatusFormatting = (worksheet, columns) => {
    let subIndex = -1, notSubIndex = -1;
    columns.forEach((col, i) => {
        if (col.label === 'Report Submitted') subIndex = i + 1;
        if (col.label === 'Report Not Submitted') notSubIndex = i + 1;
    });
    if (subIndex === -1 || notSubIndex === -1) return;
  
    worksheet.eachRow((row, rowNumber) => {
        if (rowNumber === 1) return;
        const submitted = row.getCell(subIndex).value;
        const notSubmitted = row.getCell(notSubIndex).value;
        let color = 'FFFFFF00'; // Yellow
        if (submitted == 0) color = 'FFFF0000'; // Red
        else if (notSubmitted == 0) color = 'FF00FF00'; // Green
        row.eachCell({ includeEmpty: true }, (cell) => {
            cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: color } };
        });
    });
};

export const generateGlofExcel = async (config) => {
    const { data, selectedView, toolName, columnOrder } = config;
    const baseData = data.data || data; 

    // ----------------------------------------------------
    // 1. STATUS REPORT
    // ----------------------------------------------------
    if (toolName === 'glof_status' || toolName === 'glof_daily_report_status' || toolName === 'glof_reporting_status') {
        const { columns, transformedData } = transformStatusDataForExcel(baseData, columnOrder);
        const workbook = new ExcelJS.Workbook();
        const worksheet = workbook.addWorksheet('Sheet 1');
        
        const headerRow = worksheet.addRow(columns.map(c => c.label));
        headerRow.font = { bold: true };
        columns.forEach((col, i) => { 
            const column = worksheet.getColumn(i + 1);
            column.width = Math.max(col.label.length + 5, 20); 
            // ✅ Set to Left
            column.alignment = { horizontal: 'left', vertical: 'middle' }; 
        });

        transformedData.forEach(r => worksheet.addRow(columns.map(c => r[c.label])));
        applyStatusFormatting(worksheet, columns);

        const buf = await workbook.xlsx.writeBuffer();
        saveAs(new Blob([buf]), `DailyGlofReportStatus_${getTimestamp()}.xlsx`);
        return;
    }

    // ----------------------------------------------------
    // 2. MIS REPORT
    // ----------------------------------------------------
    const viewConfig = glofMISConfig[selectedView];
    if (!viewConfig) return alert('Invalid View');

    const { headers, rows } = transformGlofMISData(data, selectedView);
    const fromDate = formatHeaderDate(baseData.from_date);
    const toDate = formatHeaderDate(baseData.to_date);

    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet('Sheet 1');

    worksheet.addRow([`Report Name: ${viewConfig.title}`]);
    worksheet.addRow([`From Date: ${fromDate}`]);
    worksheet.addRow([`To Date: ${toDate}`]);

    const totalCols = headers.length || 10; 
    worksheet.mergeCells(1, 1, 1, totalCols); 
    worksheet.mergeCells(2, 1, 2, totalCols); 
    worksheet.mergeCells(3, 1, 3, totalCols); 

    for(let i=1; i<=3; i++) {
        worksheet.getCell(i, 1).font = { bold: true, size: 11 };
        worksheet.getCell(i, 1).alignment = { vertical: 'middle', horizontal: 'left' };
    }

    worksheet.addRow(headers);
    worksheet.getRow(4).font = { bold: true }; 
    
    rows.forEach(r => worksheet.addRow(r));

    // ✅ Set all data cells strictly to the Left side
    worksheet.eachRow((row, rowNumber) => {
        if (rowNumber >= 4) { 
            row.eachCell({ includeEmpty: true }, (cell) => {
                cell.alignment = { horizontal: 'left', vertical: 'middle' };
            });
        }
    });

    const buffer = await workbook.xlsx.writeBuffer();
    saveAs(new Blob([buffer]), `MIS_${viewConfig.title.replace(/_GLOF_REPORT/g, '')}_Glof_Report_${getTimestamp()}.xlsx`);
};